export interface IntervalLike {
  id?: number | string;
  startTime: number;
  endTime: number;
}

export interface WordLike {
  word?: string;
  start?: number;
  end?: number;
  start_time?: number;
  end_time?: number;
  startTime?: number;
  endTime?: number;
}

export function getWordBounds(word: WordLike): {
  startTime: number;
  endTime: number;
} {
  const startTime = word.start || word.start_time || word.startTime || 0;
  const endTime = word.end || word.end_time || word.endTime || startTime;
  return { startTime, endTime };
}

export function buildWordKey(word: WordLike): string {
  const { startTime, endTime } = getWordBounds(word);
  return `${startTime}-${endTime}-${word.word}`;
}

/**
 * Assigns each word to the interval it belongs to.
 *
 * Pass 1: a word that strictly overlaps an interval is assigned to the
 * latest such interval (by endTime). This ensures words actually inside a
 * clip interval render in that interval's segment at full opacity, even if
 * another interval's ±contextBuffer expanded range also covers them.
 *
 * Pass 2 (fallback): a word with no strict overlap is placed in the latest
 * interval whose expanded range covers it, so it can render as dimmed
 * context alongside that interval.
 */
export function assignWordsToIntervals(
  words: WordLike[],
  intervals: IntervalLike[],
  contextBuffer: number,
  maxDuration: number = Infinity,
): Map<string, number> {
  const map = new Map<string, number>();
  if (intervals.length === 0) return map;

  const sortedIntervals = [...intervals].sort((a, b) => a.endTime - b.endTime);

  const findOriginalIndex = (sortedInterval: IntervalLike): number =>
    intervals.findIndex(
      (interval) =>
        (interval.id !== undefined && interval.id === sortedInterval.id) ||
        (interval.startTime === sortedInterval.startTime &&
          interval.endTime === sortedInterval.endTime),
    );

  for (const word of words) {
    const { startTime: wordStart, endTime: wordEnd } = getWordBounds(word);
    const wordKey = buildWordKey(word);

    let assigned = false;
    for (const sortedInterval of sortedIntervals) {
      if (
        wordStart <= sortedInterval.endTime &&
        wordEnd >= sortedInterval.startTime
      ) {
        map.set(wordKey, findOriginalIndex(sortedInterval));
        assigned = true;
      }
    }
    if (assigned) continue;

    for (const sortedInterval of sortedIntervals) {
      const expandedStart = Math.max(
        0,
        sortedInterval.startTime - contextBuffer,
      );
      const expandedEnd = Math.min(
        maxDuration,
        sortedInterval.endTime + contextBuffer,
      );
      if (wordStart <= expandedEnd && wordEnd >= expandedStart) {
        map.set(wordKey, findOriginalIndex(sortedInterval));
      }
    }
  }

  return map;
}
